import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { projectService } from '../../services/project';
import { Project } from '../../interfaces/project';
import { useToast } from '../../contexts/ToastContext';

// Estilos importados
import {
  StyledDialog,
  StyledDialogTitle,
  IconContainer,
  StyledCloseButton,
  StyledDialogContent,
  StyledTextField,
  StyledDialogActions,
  StyledCancelButton,
  StyledSaveButton,
  ErrorBox
} from './style';

interface EditProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  project: Project;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({ 
  open, 
  onClose,
  onSuccess,
  project 
}) => {
  const { showError } = useToast();
  const [title, setTitle] = useState(project?.title || '');
  const [description, setDescription] = useState(project?.description || '');
  const [loading, setLoading] = useState(false);

  const [titleError, setTitleError] = useState<string | null>(null);

  // Atualizar os campos quando o projeto mudar
  useEffect(() => {
    if (project) {
      setTitle(project.title || '');
      setDescription(project.description || '');
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar o título
    if (!title.trim()) {
      setTitleError('O título do projeto é obrigatório');
      return;
    }
    
    setLoading(true);

    setTitleError(null);
    
    try {
      if (!project?.id) {
        throw new Error('ID do projeto inválido');
      }
      
      const projectData = {
        title,
        description
      };
      
      await projectService.updateProject(project.id, projectData);
      
      setLoading(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      setLoading(false);
      showError(error.message || 'Não foi possível atualizar o projeto. Tente novamente mais tarde.');
      console.error('Erro ao atualizar projeto:', error);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <StyledDialogTitle>
          <Typography variant="h6" component="div" fontWeight={600}>
            Editar Projeto
          </Typography>
          <StyledCloseButton 
            onClick={onClose}
            disabled={loading}
            size="small"
          >
            <CloseIcon />
          </StyledCloseButton>
        </StyledDialogTitle>
        
        <StyledDialogContent>
          <IconContainer>
            <EditIcon style={{ fontSize: 30 }} />
          </IconContainer>
          
          <Typography variant="h6" align="center" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
            Atualizar informações do projeto
          </Typography>
          
          <StyledTextField
            label="Nome do Projeto"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (e.target.value.trim()) setTitleError(null);
            }}
            fullWidth
            variant="outlined"
            error={!!titleError}
            helperText={titleError}
            autoFocus
            disabled={loading}
            sx={{ mb: 3 }}
          />
          
          <StyledTextField
            label="Descrição (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            disabled={loading}
          />

        </StyledDialogContent>
        
        <StyledDialogActions>
          <StyledCancelButton 
            onClick={onClose}
            disabled={loading}
            type="button"
          >
            Cancelar
          </StyledCancelButton>
          <StyledSaveButton
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </StyledSaveButton>
        </StyledDialogActions>
      </form>
    </StyledDialog>
  );
};

export default EditProjectModal;